"use client";

import { size, theme } from "@/config/theme";
import { appLocale } from "@/recoil/appState";
import { breakpoints } from "@/utils/breakpoint";
import { SCButton, SCContainerContent } from "@/utils/styledComponents";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { canonicalPath } from "@/utils/canonicalPath";

const Locations = () => {
    const t = useTranslations("Locations");
    const router = useRouter();
    const localeAppState = useRecoilValue(appLocale);

    return (
        <SCLocations>
            <SCLocationsBackgroundMobile />
            <div className="non-mobile-view">
                <SCLocationsContent>
                    <div className="text-containers-main-container">
                        <div className="text-container">
                            <div className="text-largexlarge">
                                {t("Our Locations")}
                            </div>
                            <div className="text-medium">
                                {t("maincontainer-text")}
                            </div>
                        </div>
                        <div className="text-containers-sub-container">
                            <div className="text-container">
                                <div className="text-largexlarge">
                                    {t("subcontainer1-text1")}
                                </div>
                                <div className="text-medium">
                                    {t("subcontainer1-text2")}
                                </div>
                            </div>
                            <div className="text-container">
                                <div className="text-largexlarge">
                                    {t("subcontainer2-text1")}
                                </div>
                                <div className="text-medium">
                                    {t("subcontainer2-text2")}
                                </div>
                            </div>
                            <div className="text-container">
                                <div className="text-largexlarge">
                                    {t("subcontainer3-text1")}
                                </div>
                                <div className="text-medium">
                                    {t("subcontainer3-text2")}
                                </div>
                            </div>
                            <div className="text-container">
                                <div className="text-largexlarge">
                                    {t("subcontainer4-text1")}
                                </div>
                                <div className="text-medium">
                                    {t("subcontainer4-text2")}
                                </div>
                            </div>
                            <SCLocationsButton
                                onClick={() => {
                                    router.push(
                                        canonicalPath(
                                            `/${localeAppState}/locations`
                                        )
                                    );
                                }}
                            >
                                {t("View All Locations")}
                            </SCLocationsButton>
                        </div>
                    </div>
                </SCLocationsContent>
            </div>
        </SCLocations>
    );
};

export default Locations;

const SCLocations = styled.div`
    display: flex;
    flex-direction: column;
    background-color: #bff5ff;

    .non-mobile-view {
        display: flex;
        flex-direction: row;
        justify-content: center;
        height: 75vh;
        max-height: 650px;
        background-image: url("/media/images/background-location-generic.webp");
        background-size: cover;
        background-repeat: no-repeat;
        background-position: 0 70%;

        // Mobile view
        ${breakpoints(
            "background-image",
            "",
            [{ [size.px.sxs]: "none" }],
            "max-width"
        )};
        ${breakpoints(
            "background-color",
            "",
            [{ [size.px.sxs]: "#bff5ff" }],
            "max-width"
        )};
        ${breakpoints(
            "height",
            "",
            [{ [size.px.sxs]: "fit-content" }],
            "max-width"
        )};
    }
`;

const SCLocationsBackgroundMobile = styled.div`
    background-image: url("/media/images/background-location-generic.webp");
    height: 40dvh;
    background-size: 180%;
    background-repeat: no-repeat;
    background-position: 1% 60%;

    // Non-mobile view
    ${breakpoints(
        "display",
        "",
        [{ [(parseInt(size.px.sxs) + 1).toString()]: "none" }],
        "min-width"
    )};
`;

const SCLocationsContent = styled(SCContainerContent)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;

    // Mobile view
    ${breakpoints(
        "align-items",
        "",
        [{ [size.px.sxs]: "flex-start" }],
        "max-width"
    )};
    ${breakpoints("padding-top", "px", [{ [size.px.sxs]: "20" }], "max-width")};
    ${breakpoints(
        "padding-bottom",
        "px",
        [{ [size.px.sxs]: "40" }],
        "max-width"
    )};
    ${breakpoints(
        "height",
        "",
        [{ [size.px.sxs]: "fit-content" }],
        "max-width"
    )};

    .text-containers-main-container {
        width: 40dvw;
        height: 80%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;

        // Mobile view
        ${breakpoints("width", "", [{ [size.px.sxs]: "100%" }], "max-width")};
        ${breakpoints(
            "justify-content",
            "",
            [{ [size.px.sxs]: "flex-start" }],
            "max-width"
        )};
        > .text-container {
            ${breakpoints(
                "margin-bottom",
                "px",
                [{ [size.px.sxs]: "30" }],
                "max-width"
            )};
        }
    }

    .text-containers-sub-container {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        column-gap: ${theme["spacing-l"]};
        row-gap: ${theme["spacing-l"]};
    }

    .text-container {
        .text-largexlarge {
            font-size: ${theme["fontsize-lxl"]};
            font-weight: 500;
            margin-bottom: ${theme["spacing-m"]};
        }

        .text-medium {
            font-size: ${theme["fontsize-m"]};
            color: ${theme["color-dark-blue"]};
        }
    }
`;

const SCLocationsButton = styled(SCButton)`
    background-color: ${theme["color-white"]};
    color: ${theme["color-dark-blue"]};
    border: 1px solid ${theme["color-white"]};
    width: fit-content;
`;
